import type { ReactNode } from "react";
import { GlobalSettingsControl } from "@/components/common/GlobalSettingsControl";
import { useOrgSettings } from "@/hooks/useOrgSettings";
import { OPERATING_FRONTS, type OperatingFrontId } from "@/lib/operatingFront";
import { useOperatingFront } from "./operatingFrontContext";

function wordMark(name: string): string {
  const letter = name.trim().charAt(0).toUpperCase();
  return letter || "T";
}

export function OperatingFrontChrome({
  children,
  showSwitch = false,
}: {
  children: ReactNode;
  showSwitch?: boolean;
}) {
  const { appDisplayName, brandName } = useOrgSettings();
  const front = useOperatingFront();
  const title = (brandName || appDisplayName).trim() || "Turbo Assist";

  return (
    <div className="min-h-screen bg-[#f5f6f1] text-[#142d2d] dark:bg-zinc-950 dark:text-zinc-100">
      <header className="flex h-[76px] items-center justify-between border-b border-[#dce3dc] bg-white px-[max(5vw,20px)] dark:border-zinc-800 dark:bg-zinc-950">
        <div className="flex items-center gap-3">
          <span className="grid h-[42px] w-[42px] place-items-center rounded-[9px] bg-[#164b43] text-[18px] font-bold text-white">
            {wordMark(title)}
          </span>
          <span className="text-[22px] font-extrabold tracking-[2px]">{title}</span>
        </div>
        <div className="flex items-center gap-3">
          {showSwitch && front ? (
            <button
              type="button"
              onClick={front.returnToPicker}
              className="rounded-lg border border-[#dce3dc] bg-white px-4 py-2 text-sm font-semibold text-[#142d2d] dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100"
            >
              Switch
            </button>
          ) : null}
          <GlobalSettingsControl variant="chip" />
        </div>
      </header>
      <main className="mx-auto w-full max-w-[1180px] px-7 py-10">{children}</main>
    </div>
  );
}

export function OperatingFrontPicker({ onChoose }: { onChoose: (id: OperatingFrontId) => void }) {
  return (
    <OperatingFrontChrome>
      <p className="text-[11px] font-bold uppercase tracking-[2px] text-[#164b43] dark:text-emerald-300">
        Choose a front
      </p>
      <h1 className="mt-2 max-w-[16ch] text-[38px] font-semibold leading-[1.15] tracking-[-1.4px]">
        Where are you working?
      </h1>
      <p className="mt-3 max-w-[42rem] text-[15px] text-[#687772] dark:text-zinc-400">
        Pick a front. Your access stays the same in each one. You can switch later.
      </p>
      <div className="mt-8 grid grid-cols-[repeat(auto-fit,minmax(260px,1fr))] gap-6">
        {OPERATING_FRONTS.map((front) => (
          <section
            key={front.id}
            className="flex flex-col rounded-[13px] border border-[#dce3dc] bg-white p-[22px] dark:border-zinc-700 dark:bg-zinc-900"
          >
            <p className="text-[11px] font-bold uppercase tracking-[2px] text-[#164b43] dark:text-emerald-300">
              {front.eyebrow}
            </p>
            <h2 className="mt-3 text-[21px] font-semibold tracking-[-0.4px]">{front.title}</h2>
            <p className="mt-2 flex-1 text-[15px] text-[#687772] dark:text-zinc-400">{front.body}</p>
            <button
              type="button"
              onClick={() => onChoose(front.id)}
              className="mt-4 w-full rounded-lg bg-[#164b43] px-[22px] py-3.5 text-center font-semibold text-white"
            >
              {front.action}
            </button>
          </section>
        ))}
      </div>
    </OperatingFrontChrome>
  );
}
